import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { 
  Phone, CheckCircle2, Users, FileText, UserCheck
} from "lucide-react";
import { format, isPast } from "date-fns";

export type WorkflowStageId = 'call' | 'info' | 'documents' | 'musicians';

export interface WorkflowStage {
  id: WorkflowStageId;
  label: string;
  icon: React.ElementType;
  isComplete: boolean;
  description: string;
}

export interface WorkflowStagePanelProps {
  inquiry: any;
  contracts: any[];
  musicianAssignments: any[];
  currentStage: WorkflowStageId;
  onStageChange: (stage: WorkflowStageId) => void;
}

// Helper hook to compute workflow stages - exported for use by InquiryDetailTabs
export function useWorkflowStages(
  inquiry: any,
  contracts: any[],
  musicianAssignments: any[]
): { stages: WorkflowStage[]; currentStageId: WorkflowStageId; currentStageIndex: number } {
  return useMemo(() => {
    // Stage 1: Discovery Call - Complete when call happened (past scheduled time) or notes exist
    const hasScheduledCall = !!inquiry.scheduled_call_at;
    const callHappened = hasScheduledCall && isPast(new Date(inquiry.scheduled_call_at));
    const hasCallNotes = !!inquiry.scheduled_call_notes && inquiry.scheduled_call_notes.trim().length > 0;
    const callComplete = callHappened || hasCallNotes;
    
    // Stage 2: Collect Info - Complete when vendor contacts AND final instruments exist
    const hasVendorContacts = inquiry.vendor_contacts_json && 
      Object.values(inquiry.vendor_contacts_json).some((v: any) => v?.name || v?.email || v?.phone);
    const hasFinalInstruments = inquiry.event_dates_json?.some((e: any) => 
      e.finalInstruments && e.finalInstruments.length > 0
    );
    const infoComplete = hasVendorContacts && hasFinalInstruments;
    
    // Stage 3: Documents - Complete when Agreement AND Tech Rider AND Itinerary exist
    const hasAgreement = contracts.some(c => 
      c.contract_number?.startsWith('Agreement-') || c.contract_number?.startsWith('LSC-')
    );
    const hasTechRider = contracts.some(c => 
      c.contract_number?.startsWith('TechRider-') || c.contract_number?.startsWith('TECH-')
    );
    const hasItinerary = contracts.some(c => 
      c.contract_number?.startsWith('Timeline-') || c.contract_number?.startsWith('ITIN-')
    );
    const documentsComplete = hasAgreement && hasTechRider && hasItinerary;
    
    // Stage 4: Musicians - Complete when all final instruments have musicians assigned
    const totalInstrumentsNeeded = inquiry.event_dates_json?.reduce((sum: number, e: any) => 
      sum + (e.finalInstruments?.length || 0), 0
    ) || 0;
    const musiciansComplete = totalInstrumentsNeeded > 0 && musicianAssignments.length >= totalInstrumentsNeeded;
    
    const stages: WorkflowStage[] = [
      {
        id: 'call',
        label: 'Call',
        icon: Phone,
        isComplete: callComplete,
        description: hasScheduledCall 
          ? (callComplete ? 'Completed' : format(new Date(inquiry.scheduled_call_at), "MMM d"))
          : 'Not scheduled'
      },
      {
        id: 'info',
        label: 'Info',
        icon: Users,
        isComplete: infoComplete,
        description: `${hasVendorContacts ? '✓' : '○'} Vendors ${hasFinalInstruments ? '✓' : '○'} Ensemble`
      },
      {
        id: 'documents',
        label: 'Docs',
        icon: FileText,
        isComplete: documentsComplete,
        description: `${hasAgreement ? '✓' : '○'} ${hasTechRider ? '✓' : '○'} ${hasItinerary ? '✓' : '○'}`
      },
      {
        id: 'musicians',
        label: 'Team',
        icon: UserCheck,
        isComplete: musiciansComplete,
        description: totalInstrumentsNeeded > 0 
          ? `${musicianAssignments.length}/${totalInstrumentsNeeded}`
          : '—'
      }
    ];
    
    const currentStageIndex = stages.findIndex(s => !s.isComplete);
    const finalIndex = currentStageIndex === -1 ? stages.length - 1 : currentStageIndex;
    const currentStageId = stages[finalIndex].id;
    
    return { stages, currentStageId, currentStageIndex: finalIndex };
  }, [inquiry, contracts, musicianAssignments]);
}

// Minimal stage navigation - just clickable pills
export function WorkflowStagePanel({
  inquiry,
  contracts,
  musicianAssignments,
  currentStage,
  onStageChange
}: WorkflowStagePanelProps) {
  const { stages } = useWorkflowStages(inquiry, contracts, musicianAssignments);
  const completedCount = stages.filter(s => s.isComplete).length;

  return (
    <div className="flex items-center justify-between gap-2 px-1 py-1.5 bg-muted/30 rounded-lg">
      {/* Stage Pills */}
      <div className="flex items-center gap-1">
        {stages.map((stage) => {
          const Icon = stage.icon;
          const isActive = currentStage === stage.id;
          
          return (
            <button
              key={stage.id}
              onClick={() => onStageChange(stage.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                isActive 
                  ? 'bg-primary text-primary-foreground shadow-sm' 
                  : stage.isComplete 
                    ? 'bg-green-500/10 text-green-700 hover:bg-green-500/20' 
                    : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {stage.isComplete && !isActive ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <Icon className="h-3 w-3" />
              )}
              <span>{stage.label}</span>
            </button>
          );
        })}
      </div>
      
      {/* Progress indicator */}
      <Badge variant="outline" className="text-[10px] h-5">
        {completedCount}/{stages.length} complete
      </Badge>
    </div>
  );
}
